/* eslint react-hooks/exhaustive-deps: "warn" */


import { useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { Icon } from 'ui/atoms/Icon/Icon';
import { Image } from 'ui/atoms/Image';
import { Section } from 'ui/molecules/Section/Section';

import styles from './AIProductCompareSection.module.scss';

const CheckIcon = () => (
  <svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">
    <path d="M1 4.4 4 7.5 10 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CrossIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
    <path d="M1 1l7 7M8 1 1 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DashIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
    <path d="M1 4.5h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

type Row = {
  capability: string;
  regis: string;
  standard: string;
  legacy: string;
};

const ROWS: Row[] = [
  {
    capability: 'Is this company actively trading?',
    regis: 'Real-time registry check',
    standard: 'Hallucinates or uses outdated data',
    legacy: 'Manual lookup required',
  },
  {
    capability: 'Who are the shareholders?',
    regis: 'Full ownership chain, sourced',
    standard: 'Guesses — often wrong',
    legacy: 'Available, but slow and costly',
  },
  {
    capability: 'Where did this answer come from?',
    regis: 'Registry source on every line',
    standard: 'No verifiable source',
    legacy: 'Data partners — does not provide source',
  },
  {
    capability: 'Show 20 years of filed financials',
    regis: 'Instant, straight from the registry',
    standard: 'Fabricated or unavailable',
    legacy: 'Billed per report',
  },
  {
    capability: 'Build a company list by sector, country, size',
    regis: 'One query, fully sourced',
    standard: 'Makes up the list',
    legacy: 'Manual querying across multiple filters',
  },
  {
    capability: 'See every company a person is connected to, worldwide',
    regis: 'Every directorship & shareholding, sourced',
    standard: "Can't map it reliably",
    legacy: 'Premium add-on',
  },
];

export const AIProductCompareSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.15 });

  return (
    <Section
      fullWidth
      introBlockClassName={styles.intro}
      title={
        <>
          General AI guesses. Legacy data hides its sources.
          <br />
          Regis answers straight from the registry.
        </>
      }
      className={styles.section}
    >
      <div className={styles.scroll}>
        <div ref={ref} className={cn(styles.wrap, revealed && styles.revealed)}>
          <div className={cn(styles.row, styles.rowHead)}>
            <div className={styles.cell}>Capability</div>

            <div className={cn(styles.cell, styles.cellRegis)}>
              <span className={styles.regisName}>Regis</span>

              <span className={styles.logos}>
                <Icon type="logo-new-short" className={styles.logo} />
              </span>
            </div>

            <div className={styles.cell}>
              <span>Standard AI</span>

              <span className={styles.chips}>
                <span className={styles.chip}>ChatGPT</span>
                <span className={styles.chip}>Claude</span>
                <span className={styles.chip}>Gemini</span>
              </span>
            </div>

            <div className={styles.cell}>
              <span>Legacy Data</span>

              <span className={styles.logos}>
                <Image width={40} height={24} src="img/customers/dnb.png" alt="Dun &amp; Bradstreet logo" />
                <Image width={70} height={16} src="img/customers/moodys.png" alt="Moody's logo" />
              </span>
            </div>
          </div>

          {ROWS.map((row) => (
            <div key={row.capability} className={styles.row}>
              <div className={styles.cell}>{row.capability}</div>

              <div className={cn(styles.cell, styles.cellAnswer, styles.cellRegis)}>
                <span className={cn(styles.status, styles.statusGood)}>
                  <CheckIcon />
                </span>
                {row.regis}
              </div>

              <div className={cn(styles.cell, styles.cellAnswer)}>
                <span className={cn(styles.status, styles.statusBad)}>
                  <CrossIcon />
                </span>
                {row.standard}
              </div>

              <div className={cn(styles.cell, styles.cellAnswer)}>
                <span className={cn(styles.status, styles.statusNeutral)}>
                  <DashIcon />
                </span>
                {row.legacy}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
